<script lang="ts">
const __DsBlock = {
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input.name',
	description: '$t:blocks.input.description',
	type: 'input',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => {
			const types = ['text'];
			if (isPrimitiveTypeString(primitiveType)) types.push('identifier');
			return types;
		},
	},

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'type',
			label: '$t:blocks.common.props.type',
			type: 'enum',
			values: ['text', 'password', 'email', 'number', 'tel', 'url', 'date', 'time', 'datetime-local', 'month', 'week'],
		},
		{
			name: 'placeholder',
			label: '$t:blocks.common.props.placeholder',
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'leadingIcon',
			label: '$t:blocks.common.props.leading_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'trailingIcon',
			label: '$t:blocks.common.props.trailing_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'loading',
			label: '$t:blocks.common.props.loading',
		},
		{
			name: 'block',
			label: '$t:blocks.common.props.block',
		},
		{
			name: 'autocomplete',
			label: '$t:blocks.common.props.autocomplete',
		},
		{ name: 'modelModifiers', hidden: true },
	],
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { InputProps } from '#layers/ui/public';
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';

import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { isPrimitiveTypeString } from '#layers/data-model/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useTranslationUtils } from '#layers/i18n/public';
import { useDisabledNotice } from '#layers/ui/public';

type InputType = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'date' | 'time' | 'datetime-local' | 'month' | 'week';

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

interface Props extends InputBlockProps, Pick<
	InputProps<any>,
	'placeholder' | 'variant' | 'size' | 'leadingIcon' | 'trailingIcon' | 'loading' | 'autocomplete' | 'modelModifiers'
> {
	type?: InputType;

	avatar?: string;

	/**
	 * Render the input full width
	 */
	block?: boolean;
}

const model = defineModel<string>();

;

const forwarded = reactivePick(props, 'type', 'variant', 'size', 'leadingIcon', 'trailingIcon', 'loading', 'block', 'autocomplete');
const { translateString } = useTranslationUtils();
const translatedPlaceholder = computed(() => translateString(props.placeholder));
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInput
			v-bind="forwarded"
			v-model="modelValue"
			:model-modifiers="modelModifiers ?? {}"
			:placeholder="translatedPlaceholder"
			:readonly
			:aria-readonly="readonly"
			:disabled
			:avatar="avatar ? {
				src: avatar,
			} : undefined"
			class="min-w-0"
		/>
	</DTooltip>
</template>
